/**
 * UI-facing attribute registry - one entry per listing attribute (New,
 * Open House, Favorites, Alerts, Price Change), giving CNTLDrawer,
 * UnifiedDrawer, Map.ts's badge rendering, and ListingsDrawer's sections
 * one shared source for label/icon/color/matching/sorting instead of each
 * hand-copying the same 4 (soon 5) names. See @slowmaui/shared-types'
 * attributes.ts for the pure matching logic this builds on.
 *
 * Icons are the exact paths already used on the map (Map.ts's old
 * BADGE_SYMBOLS) - the most recently tuned/reviewed set. CNTLDrawer and
 * UnifiedDrawer previously each drew their own slightly different icon per
 * attribute; this intentionally unifies them to one look everywhere.
 */
import { ATTRIBUTE_KIND, attributeMatches } from '@slowmaui/shared-types';
/** Parses "11:00 AM" / "1:30 PM" style strings into 24-hour hours/minutes. */
function parseOpenHouseTime(timeStr) {
    if (!timeStr)
        return null;
    const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match)
        return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12)
        hours += 12;
    if (period === 'AM' && hours === 12)
        hours = 0;
    return { hours, minutes };
}
function sortByListDateDesc(a, b) {
    const dateA = a.listDate ? new Date(a.listDate).getTime() : 0;
    const dateB = b.listDate ? new Date(b.listDate).getTime() : 0;
    return dateB - dateA;
}
/** Chronological (date, then start time) - soonest first. Listings with an unparseable or missing start time sort last within their date. */
function sortByOpenHouseDateAsc(a, b) {
    const dateA = a.openHouse?.date ?? '';
    const dateB = b.openHouse?.date ?? '';
    if (dateA !== dateB)
        return dateA.localeCompare(dateB);
    const startA = parseOpenHouseTime(a.openHouse?.start);
    const startB = parseOpenHouseTime(b.openHouse?.start);
    const minutesA = startA ? startA.hours * 60 + startA.minutes : Infinity;
    const minutesB = startB ? startB.hours * 60 + startB.minutes : Infinity;
    return minutesA - minutesB;
}
function sortByPriceChangeDateDesc(a, b) {
    const dateA = a.priceChangeDate ? new Date(a.priceChangeDate).getTime() : 0;
    const dateB = b.priceChangeDate ? new Date(b.priceChangeDate).getTime() : 0;
    return dateB - dateA;
}
// Inner SVG markup only (no wrapping <svg> tag) - each consumer (CNTL
// checkbox, map badge, drawer handle, listing card) wraps this in its own
// <svg viewBox="0 0 24 24" fill="currentColor" ...sizing/class...> since
// each needs different dimensions and a different way of applying color
// (CSS class vs inline style).
const NEW_ICON = '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>';
const OPEN_HOUSE_ICON = '<path d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3z"/>';
const FAVORITES_ICON = '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>';
const ALERTS_ICON = '<path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>';
// Briefcase - "your agent picked this out for you", distinct from the
// person-curated-but-self-driven Favorites/Alerts icons above.
const AGENT_PICKS_ICON = '<path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>';
// Bookmark - "your agent chose to feature this on their site," distinct
// from the star-burst New icon and the briefcase Agent Picks icon above.
const FEATURED_ICON = '<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>';
// Bidirectional - price changes aren't always down, so the attribute-level
// icon (checkbox/badge/handle) stays neutral; a per-listing arrow shows the
// actual direction on the drawer card once it's known for that listing.
// One continuous shaft with an arrowhead at each end (not two separate
// chevrons with a gap between them, which read as two disconnected specks
// at small badge sizes).
const PRICE_CHANGE_ICON = '<path d="M12 2l5 7h-3v6h3l-5 7-5-7h3V9H7z"/>';
export const ATTRIBUTE_REGISTRY = [
    {
        id: 'favorites',
        kind: ATTRIBUTE_KIND.favorites,
        label: 'Favorites',
        priority: 0,
        icon: FAVORITES_ICON,
        color: '#ef4444', // Red
        matches: (facts, idSets) => attributeMatches('favorites', facts, idSets),
    },
    {
        id: 'alerts',
        kind: ATTRIBUTE_KIND.alerts,
        label: 'Alerts',
        priority: 1,
        icon: ALERTS_ICON,
        color: '#f59e0b', // Amber
        matches: (facts, idSets) => attributeMatches('alerts', facts, idSets),
    },
    {
        id: 'newListing',
        kind: ATTRIBUTE_KIND.newListing,
        label: 'New',
        priority: 2,
        icon: NEW_ICON,
        color: '#eab308', // Yellow
        matches: (facts, idSets) => attributeMatches('newListing', facts, idSets),
        sortCompare: sortByListDateDesc,
    },
    {
        id: 'priceChange',
        kind: ATTRIBUTE_KIND.priceChange,
        label: 'Price Change',
        priority: 3,
        icon: PRICE_CHANGE_ICON,
        color: '#3b82f6', // Blue
        matches: (facts, idSets) => attributeMatches('priceChange', facts, idSets),
        sortCompare: sortByPriceChangeDateDesc,
    },
    {
        id: 'openHouse',
        kind: ATTRIBUTE_KIND.openHouse,
        label: 'Open House',
        priority: 4,
        icon: OPEN_HOUSE_ICON,
        // #ede9fe (the ListingsDrawer open-house label's old background) read
        // too washed-out once deployed. This is a brighter, still fully-
        // saturated violet - bold purple, with enough luminance to contrast
        // against the dark green neighborhood label and satellite imagery.
        color: '#a78bfa', // Violet
        matches: (facts, idSets) => attributeMatches('openHouse', facts, idSets),
        sortCompare: sortByOpenHouseDateAsc,
    },
    {
        id: 'agentPicks',
        kind: ATTRIBUTE_KIND.agentPicks,
        label: 'Agent Picks',
        // Last: it's reached exclusively via a story link (which already
        // isolates the view to just these items on its own, see
        // CNTLDrawer.showOnlyAttribute), not picked from the grid like the
        // others - so it doesn't need a prominent position among them.
        priority: 5,
        icon: AGENT_PICKS_ICON,
        // Agent orange - the same accent used everywhere else something is an
        // agent's own addition (apps/agent's Agent Tools handle, the Content
        // tab), so it reads consistently as "your agent did this" wherever it
        // shows up, in either app.
        color: '#f97316',
        matches: (facts, idSets) => attributeMatches('agentPicks', facts, idSets),
    },
    {
        id: 'featured',
        kind: ATTRIBUTE_KIND.featured,
        label: 'Featured',
        // Same grouping logic as Agent Picks above (reached via a link, not
        // picked from the grid) - but not exclusive the way Agent Picks is,
        // since Featured is meant to sit alongside everything else rather
        // than isolate the view.
        priority: 6,
        icon: FEATURED_ICON,
        // Teal - deliberately distinct from Agent Picks' orange (both are
        // "agent-curated," but different things: a one-off message vs. a
        // persistent showcase) and from every other attribute's color.
        color: '#14b8a6',
        matches: (facts, idSets) => attributeMatches('featured', facts, idSets),
    },
];
export function getAttributeEntry(id) {
    const entry = ATTRIBUTE_REGISTRY.find((a) => a.id === id);
    if (!entry)
        throw new Error(`Unknown attribute id: ${id}`);
    return entry;
}
//# sourceMappingURL=registry.js.map